import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, LibraryBooks as ReviewIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';

const BillGenerator = ({ clonePayload, onClearClone }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [gstType, setGstType] = useState('instate'); // 'nogst', 'instate', 'outofstate'
  const [billData, setBillData] = useState({
    billNo: '',
    billDate: format(new Date(), 'dd/MM/yyyy'),
    placeOfSupply: '',
    remarks: '',
    customer: {
      name: '',
      gstin: '',
      address: '',
      contactPerson: '',
      contactNo: ''
    },
    items: [
      {
        lrDate: '',
        lrNo: '',
        vehicleNo: '',
        fromLocation: '',
        toLocation: '',
        freightCharge: '',
        documentCharges: '0',
        loadingCharges: '0',
        doorDeliveryCharges: '0',
        haltingCharges: '0',
        otherCharges: '0',
        amount: '0'
      }
    ]
  });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [lastBillInfo, setLastBillInfo] = useState([]);
  
  // V2 UI States
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [companyName, setCompanyName] = useState('');

  const toDateInputFormat = (ddmmyyyy) => {
    if (!ddmmyyyy || ddmmyyyy.length < 10) return '';
    const [day, month, year] = ddmmyyyy.split('/');
    return `${year}-${month}-${day}`;
  };

  const fromDateInputFormat = (yyyymmdd) => {
    if (!yyyymmdd) return '';
    const [year, month, day] = yyyymmdd.split('-');
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    fetchCustomers();
    fetchLastBillInfo();
    resolveCompanyName();
  }, []);

  useEffect(() => {
    if (clonePayload && customers.length > 0) {
      const customer = customers.find(c => c.name === clonePayload.customer_name);
      if (customer) {
        setSelectedCustomer(customer.id);
      }
      setGstType(clonePayload.gst_type || 'instate');
      setBillData({
        billNo: '', // Auto-generate
        billDate: format(new Date(), 'dd/MM/yyyy'),
        placeOfSupply: clonePayload.place_of_supply || '',
        remarks: clonePayload.remarks || '',
        customer: {
          name: clonePayload.customer_name || '',
          gstin: customer?.gstin || '',
          address: customer?.address || '',
          contactPerson: customer?.contactPerson || '',
          contactNo: customer?.contactNo || ''
        },
        items: clonePayload.items ? clonePayload.items.map(item => ({
          ...item,
          lrDate: format(new Date(), 'dd/MM/yyyy') // reset to today
        })) : [
          {
            lrDate: format(new Date(), 'dd/MM/yyyy'),
            lrNo: '',
            vehicleNo: '',
            fromLocation: '',
            toLocation: '',
            freightCharge: '',
            documentCharges: '0',
            loadingCharges: '0',
            doorDeliveryCharges: '0',
            haltingCharges: '0',
            otherCharges: '0',
            amount: '0'
          }
        ]
      });

      if (onClearClone) {
        onClearClone();
      }
    }
  }, [clonePayload, customers, onClearClone]);

  const resolveCompanyName = async () => {
    try {
      const companyId = localStorage.getItem('selectedCompanyId');
      if (companyId) {
        const { data } = await supabase.from('companies').select('name').eq('id', companyId).single();
        if (data) setCompanyName(data.name);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCustomers = async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchLastBillInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('last_bill_view')
        .select('*');
      
      if (error) throw error;
      setLastBillInfo(data || []);
    } catch (error) {
      console.error('Error fetching last bill info:', error);
    }
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customerId);
      setBillData(prev => ({
        ...prev,
        customer: {
          name: customer.name,
          gstin: customer.gstin,
          address: customer.address,
          contactPerson: customer.contactPerson,
          contactNo: customer.contactNo
        }
      }));
    } else {
      setSelectedCustomer('');
      setBillData(prev => ({
        ...prev,
        customer: { name: '', gstin: '', address: '', contactPerson: '', contactNo: '' }
      }));
    }
  };

  const handleBillDataChange = (field, value) => {
    setBillData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      if (field === 'billDate') {
        newData.items = prev.items.map(item => ({
          ...item,
          lrDate: value
        }));
      }
      
      return newData;
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...billData.items];
    newItems[index][field] = value;
    
    if (['freightCharge', 'documentCharges', 'loadingCharges', 'doorDeliveryCharges', 'haltingCharges', 'otherCharges'].includes(field)) {
      const item = newItems[index];
      const amount = 
        parseFloat(item.freightCharge || 0) +
        parseFloat(item.documentCharges || 0) +
        parseFloat(item.loadingCharges || 0) +
        parseFloat(item.doorDeliveryCharges || 0) +
        parseFloat(item.haltingCharges || 0) +
        parseFloat(item.otherCharges || 0);
      newItems[index].amount = amount.toString();
    }
    
    setBillData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const addItem = () => {
    const vehicleNo = billData.items.length > 0 ? billData.items[0].vehicleNo : '';
    setBillData(prev => ({
      ...prev,
      items: [...prev.items, {
        lrDate: prev.billDate,
        lrNo: '',
        vehicleNo: vehicleNo,
        fromLocation: '',
        toLocation: '',
        freightCharge: '',
        documentCharges: '0',
        loadingCharges: '0',
        doorDeliveryCharges: '0',
        haltingCharges: '0',
        otherCharges: '0',
        amount: '0'
      }]
    }));
  };

  const removeItem = (index) => {
    setBillData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotals = () => {
    const taxableValue = billData.items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    
    let sgst = 0;
    let cgst = 0;
    let igst = 0;
    
    if (gstType === 'instate') {
      sgst = taxableValue * 0.09;
      cgst = taxableValue * 0.09;
    } else if (gstType === 'outofstate') {
      igst = taxableValue * 0.18;
    }
    
    const grandTotal = taxableValue + sgst + cgst + igst;
    
    return {
      taxableValue: taxableValue.toFixed(2),
      sgst: sgst.toFixed(2),
      cgst: cgst.toFixed(2),
      igst: igst.toFixed(2),
      grandTotal: grandTotal.toFixed(2)
    };
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    if (num === 0) return 'Zero Rupees Only';
    
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const remainder = num % 1000;
    
    let result = '';
    if (crore) result += convertLessThanThousand(crore) + ' Crore ';
    if (lakh) result += convertLessThanThousand(lakh) + ' Lakh ';
    if (thousand) result += convertLessThanThousand(thousand) + ' Thousand ';
    if (remainder) result += convertLessThanThousand(remainder);
    
    return result.trim() + ' Rupees Only';
  };

  const handleReviewBill = () => {
    if (!billData.customer.name) {
      setAlert({ show: true, message: 'Please select a customer first', severity: 'error' });
      return;
    }
    const invalidItems = billData.items.some(item => !item.fromLocation || !item.toLocation || !item.amount || parseFloat(item.amount) === 0);
    if (invalidItems) {
      setAlert({ show: true, message: 'Please complete routes and charge details for all line items', severity: 'warning' });
      return;
    }
    resolveCompanyName();
    setOpenReviewDialog(true);
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      // 1. Determine active company ID
      let companyId = localStorage.getItem('selectedCompanyId');
      if (!companyId) {
        const { data: companies } = await supabase.from('companies').select('id');
        if (companies && companies.length > 0) {
          companyId = companies[0].id;
          localStorage.setItem('selectedCompanyId', companyId);
        }
      }

      if (!companyId) {
        throw new Error('No company configured. Please configure a company in Company Settings first.');
      }

      // 2. Fetch full company details
      const { data: companyInfo, error: companyErr } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();
      
      if (companyErr) throw companyErr;

      // 3. Atomically increment and get bill number if not provided manually
      let finalBillNo = billData.billNo;
      if (!finalBillNo) {
        const { data: nextNo, error: rpcErr } = await supabase
          .rpc('get_next_bill_number', { p_company_id: companyId });
        
        if (rpcErr) throw rpcErr;
        finalBillNo = nextNo.toString();
        
        // Update input field
        setBillData(prev => ({ ...prev, billNo: finalBillNo }));
      }

      const totals = calculateTotals();
      const amountInWords = numberToWords(Math.floor(parseFloat(totals.grandTotal)));

      // 4. Log/Write the bill history record
      const { data: sessionData } = await supabase.auth.getSession();
      const sessionUser = sessionData?.session?.user;
      
      // Fetch user profile username
      const { data: profile } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', sessionUser?.id)
        .single();
        
      const username = profile?.username || sessionUser?.email || 'unknown';

      // Insert record to get generated primary key ID, passing along remarks, supplies, and items
      const { data: insertedBill, error: insertErr } = await supabase
        .from('bills')
        .insert({
          bill_number: finalBillNo,
          company_id: companyId,
          customer_id: selectedCustomer || null,
          customer_name: billData.customer.name,
          total_amount: parseFloat(totals.grandTotal),
          gst_type: gstType,
          generated_by: username,
          place_of_supply: billData.placeOfSupply,
          remarks: billData.remarks,
          items: billData.items
        })
        .select('id')
        .single();
      
      if (insertErr) throw insertErr;

      // 5. Get the public signature URL from Supabase Storage
      const { data: storageData } = supabase.storage
        .from('signatures')
        .getPublicUrl(`signature-${companyId}.png`);
      const signatureUrl = storageData?.publicUrl;

      // 6. Invoke the Supabase Edge Function to generate the PDF
      const { data: pdfBlob, error: fnError } = await supabase.functions.invoke(
        'generate-bill-pdf',
        {
          body: {
            billData: {
              ...billData,
              billNo: finalBillNo,
              billId: insertedBill.id, // Pass DB ID
              totals,
              amountInWords
            },
            companyInfo,
            signatureUrl,
            gstType
          }
        }
      );

      if (fnError) throw fnError;
      if (!pdfBlob) throw new Error("No PDF data returned from service.");

      // 7. Trigger file download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bill-${finalBillNo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setAlert({ show: true, message: 'Bill generated successfully!', severity: 'success' });
      fetchLastBillInfo();
      setOpenReviewDialog(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setAlert({ show: true, message: error.message || 'Error generating PDF', severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.01)' }}>
      <Typography variant="h4" fontWeight="700" gutterBottom>
        Generate Bill
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Enter billing parameters and line items.
      </Typography>

      {alert.show && (
        <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, show: false })} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      {/* Last Bill Info Notification */}
      {lastBillInfo && lastBillInfo.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {lastBillInfo.map((bill, index) => (
            <Alert key={index} severity="info" sx={{ mb: 1, borderRadius: 2 }}>
              Last invoice for <strong>{bill.company_name}</strong>: <strong>#{bill.bill_number}</strong> by{' '}
              <strong>{bill.generated_by}</strong>
            </Alert>
          ))}
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Bill Details */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Bill Number (Leave blank to auto-generate)"
            value={billData.billNo}
            onChange={(e) => handleBillDataChange('billNo', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Bill Date"
            type="date"
            value={toDateInputFormat(billData.billDate)}
            onChange={(e) => handleBillDataChange('billDate', fromDateInputFormat(e.target.value))}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Place of Supply"
            value={billData.placeOfSupply}
            onChange={(e) => handleBillDataChange('placeOfSupply', e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Remarks (Optional)"
            value={billData.remarks}
            onChange={(e) => handleBillDataChange('remarks', e.target.value)}
            multiline
            rows={2}
          />
        </Grid>

        {/* Autocomplete Customer Selection */}
        <Grid item xs={12}>
          <Autocomplete
            options={customers}
            getOptionLabel={(option) => option.name || ''}
            value={customers.find(c => c.id === selectedCustomer) || null}
            onChange={(event, newValue) => {
              handleCustomerSelect(newValue ? newValue.id : '');
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Search / Select Customer" 
                variant="outlined" 
                required 
              />
            )}
          />
        </Grid>

        {/* Customer Details (Auto-filled) */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Customer Name"
            value={billData.customer.name}
            InputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Customer GSTIN"
            value={billData.customer.gstin}
            InputProps={{ readOnly: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            value={billData.customer.address}
            InputProps={{ readOnly: true }}
            multiline
            rows={2}
          />
        </Grid>

        {/* Line Items Table */}
        <Grid item xs={12}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' }, 
            gap: 2, 
            mb: 2 
          }}>
            <Typography variant="h6" fontWeight="700">Line Items</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
              <FormControl fullWidth size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
                <InputLabel>GST Type</InputLabel>
                <Select
                  value={gstType}
                  onChange={(e) => setGstType(e.target.value)}
                  label="GST Type"
                >
                  <MenuItem value="instate">In State (CGST + SGST)</MenuItem>
                  <MenuItem value="outofstate">Out of State (IGST)</MenuItem>
                  <MenuItem value="nogst">No GST</MenuItem>
                </Select>
              </FormControl>
              {!isMobile && (
                <Button startIcon={<AddIcon />} variant="contained" onClick={addItem} sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, minWidth: '120px' }}>
                  Add Row
                </Button>
              )}
            </Box>
          </Box>

          {/* Desktop View - Table */}
          {!isMobile && (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
              <Table size="small" sx={{ '& .MuiTableCell-root': { padding: '8px 12px' } }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ minWidth: '140px', fontWeight: '600' }}>LR Date</TableCell>
                    <TableCell sx={{ minWidth: '100px', fontWeight: '600' }}>LR No.</TableCell>
                    <TableCell sx={{ minWidth: '120px', fontWeight: '600' }}>Vehicle No.</TableCell>
                    <TableCell sx={{ minWidth: '100px', fontWeight: '600' }}>From</TableCell>
                    <TableCell sx={{ minWidth: '100px', fontWeight: '600' }}>To</TableCell>
                    <TableCell sx={{ minWidth: '90px', fontWeight: '600' }}>Freight</TableCell>
                    <TableCell sx={{ minWidth: '90px', fontWeight: '600' }}>Loading</TableCell>
                    <TableCell sx={{ minWidth: '90px', fontWeight: '600' }}>Halting</TableCell>
                    <TableCell sx={{ minWidth: '80px', fontWeight: '600' }}>Other</TableCell>
                    <TableCell sx={{ minWidth: '90px', fontWeight: '600' }}>Amount</TableCell>
                    <TableCell sx={{ width: '50px', fontWeight: '600' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {billData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          size="small"
                          type="date"
                          value={toDateInputFormat(item.lrDate)}
                          onChange={(e) => handleItemChange(index, 'lrDate', fromDateInputFormat(e.target.value))}
                          InputProps={{ sx: { fontSize: '0.875rem' } }}
                          sx={{ minWidth: '140px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.lrNo}
                          onChange={(e) => handleItemChange(index, 'lrNo', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.875rem' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.vehicleNo}
                          onChange={(e) => handleItemChange(index, 'vehicleNo', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.875rem' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.fromLocation}
                          onChange={(e) => handleItemChange(index, 'fromLocation', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.875rem' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.toLocation}
                          onChange={(e) => handleItemChange(index, 'toLocation', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.875rem' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          value={item.freightCharge}
                          onChange={(e) => handleItemChange(index, 'freightCharge', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.875rem' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          value={item.loadingCharges}
                          onChange={(e) => handleItemChange(index, 'loadingCharges', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.875rem' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          value={item.haltingCharges}
                          onChange={(e) => handleItemChange(index, 'haltingCharges', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.875rem' } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          value={item.otherCharges}
                          onChange={(e) => handleItemChange(index, 'otherCharges', e.target.value)}
                          InputProps={{ sx: { fontSize: '0.875rem' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: '700', color: '#0f172a' }}>₹{item.amount}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeItem(index)}
                          disabled={billData.items.length === 1}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Mobile View - Cards */}
          {isMobile && (
            <Box>
              {billData.items.map((item, index) => (
                <Card key={index} sx={{ mb: 2, position: 'relative', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Item #{index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeItem(index)}
                        disabled={billData.items.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="LR Date"
                          type="date"
                          value={toDateInputFormat(item.lrDate)}
                          onChange={(e) => handleItemChange(index, 'lrDate', fromDateInputFormat(e.target.value))}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="LR No."
                          value={item.lrNo}
                          onChange={(e) => handleItemChange(index, 'lrNo', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Vehicle No."
                          value={item.vehicleNo}
                          onChange={(e) => handleItemChange(index, 'vehicleNo', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="From Location"
                          value={item.fromLocation}
                          onChange={(e) => handleItemChange(index, 'fromLocation', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="To Location"
                          value={item.toLocation}
                          onChange={(e) => handleItemChange(index, 'toLocation', e.target.value)}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }}>Charges</Divider>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Freight Charge"
                          type="text"
                          value={item.freightCharge}
                          onChange={(e) => handleItemChange(index, 'freightCharge', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Loading Charges"
                          type="text"
                          value={item.loadingCharges}
                          onChange={(e) => handleItemChange(index, 'loadingCharges', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Halting Charges"
                          type="text"
                          value={item.haltingCharges}
                          onChange={(e) => handleItemChange(index, 'haltingCharges', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Other Charges"
                          type="text"
                          value={item.otherCharges}
                          onChange={(e) => handleItemChange(index, 'otherCharges', e.target.value)}
                        />
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box sx={{ bgcolor: 'rgba(99, 102, 241, 0.05)', p: 1.5, borderRadius: 1.5, textAlign: 'center' }}>
                          <Typography variant="body2" color="#6366f1" fontWeight="bold">
                            Total Amount: ₹{item.amount}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              {isMobile && (
                <Button 
                  fullWidth 
                  startIcon={<AddIcon />} 
                  variant="outlined" 
                  onClick={addItem} 
                  sx={{ mt: 1, mb: 2, color: '#6366f1', borderColor: '#6366f1', fontWeight: '600' }}
                >
                  Add Row
                </Button>
              )}
            </Box>
          )}
        </Grid>

        {/* Totals */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Box sx={{ width: { xs: '100%', sm: '350px' }, p: 2, bgcolor: '#f8fafc', borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Taxable Value:</Typography>
                <Typography variant="body2" fontWeight="600" sx={{ wordBreak: 'break-word', pl: 2, textAlign: 'right' }}>₹{totals.taxableValue}</Typography>
              </Box>
              
              {gstType === 'instate' && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                    <Typography variant="body2" color="text.secondary">SGST @ 9%:</Typography>
                    <Typography variant="body2" fontWeight="600" sx={{ wordBreak: 'break-word', pl: 2, textAlign: 'right' }}>₹{totals.sgst}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                    <Typography variant="body2" color="text.secondary">CGST @ 9%:</Typography>
                    <Typography variant="body2" fontWeight="600" sx={{ wordBreak: 'break-word', pl: 2, textAlign: 'right' }}>₹{totals.cgst}</Typography>
                  </Box>
                </>
              )}
              
              {gstType === 'outofstate' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1 }}>
                  <Typography variant="body2" color="text.secondary">IGST @ 18%:</Typography>
                  <Typography variant="body2" fontWeight="600" sx={{ wordBreak: 'break-word', pl: 2, textAlign: 'right' }}>₹{totals.igst}</Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 1.5 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="700">Grand Total:</Typography>
                <Typography variant="h6" fontWeight="700" color="#6366f1" sx={{ wordBreak: 'break-all', pl: 2, textAlign: 'right' }}>
                  ₹{totals.grandTotal}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Review Button */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<ReviewIcon />}
              onClick={handleReviewBill}
              sx={{ bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' }, px: 4, py: 1.5, borderRadius: 2.5, fontWeight: '600' }}
            >
              Review Invoice Details
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Confirmation Review Modal */}
      <Dialog 
        open={openReviewDialog} 
        onClose={() => setOpenReviewDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: '700', pb: 1, fontSize: '1.25rem' }}>Confirm Invoice Details</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please review the billing calculations and coordinates before generating the final PDF.
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ display: 'block', mb: 0.5 }}>COMPANY (ISSUER)</Typography>
              <Typography variant="body2" fontWeight="600">{companyName || 'Active Company'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ display: 'block', mb: 0.5 }}>CUSTOMER (BILLED TO)</Typography>
              <Typography variant="body2" fontWeight="600">{billData.customer.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{billData.customer.address}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.5 }}>GSTIN: {billData.customer.gstin}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ display: 'block', mb: 0.5 }}>BILL NUMBER</Typography>
              <Typography variant="body2" fontWeight="600">{billData.billNo || 'Auto-Generate'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ display: 'block', mb: 0.5 }}>BILL DATE</Typography>
              <Typography variant="body2" fontWeight="600">{billData.billDate}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ display: 'block', mb: 0.5 }}>PLACE OF SUPPLY</Typography>
              <Typography variant="body2" fontWeight="600">{billData.placeOfSupply || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="caption" color="text.secondary" fontWeight="700" sx={{ display: 'block', mb: 0.5 }}>GST MODE</Typography>
              <Typography variant="body2" fontWeight="600" style={{ textTransform: 'capitalize' }}>
                {gstType === 'instate' ? 'In State (CGST/SGST)' : gstType === 'outofstate' ? 'Out of State (IGST)' : 'No GST'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="700" sx={{ mb: 1.5 }}>Line Items Summary</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: '600' }}>Vehicle No</TableCell>
                  <TableCell sx={{ fontWeight: '600' }}>Route</TableCell>
                  <TableCell align="right" sx={{ fontWeight: '600' }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billData.items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{item.vehicleNo || 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{item.fromLocation} to {item.toLocation}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: '600', fontSize: '0.85rem' }}>₹{item.amount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', bgcolor: '#f8fafc', p: 2, borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <Box sx={{ width: '250px' }}>
              <Grid container spacing={1}>
                <Grid item xs={7}><Typography variant="body2" color="text.secondary">Taxable Value:</Typography></Grid>
                <Grid item xs={5}><Typography variant="body2" align="right" fontWeight="600">₹{totals.taxableValue}</Typography></Grid>
                {gstType === 'instate' && (
                  <>
                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">CGST (9%):</Typography></Grid>
                    <Grid item xs={5}><Typography variant="body2" align="right" fontWeight="600">₹{totals.cgst}</Typography></Grid>
                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">SGST (9%):</Typography></Grid>
                    <Grid item xs={5}><Typography variant="body2" align="right" fontWeight="600">₹{totals.sgst}</Typography></Grid>
                  </>
                )}
                {gstType === 'outofstate' && (
                  <>
                    <Grid item xs={7}><Typography variant="body2" color="text.secondary">IGST (18%):</Typography></Grid>
                    <Grid item xs={5}><Typography variant="body2" align="right" fontWeight="600">₹{totals.igst}</Typography></Grid>
                  </>
                )}
                <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
                <Grid item xs={7}><Typography variant="subtitle1" fontWeight="700">Grand Total:</Typography></Grid>
                <Grid item xs={5}><Typography variant="subtitle1" fontWeight="700" align="right" color="#6366f1">₹{totals.grandTotal}</Typography></Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setOpenReviewDialog(false)} variant="text" color="inherit">
            Cancel / Edit Form
          </Button>
          <Button 
            onClick={generatePDF} 
            variant="contained" 
            disabled={generating}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, fontWeight: '600', px: 3 }}
          >
            {generating ? 'Generating PDF...' : 'Confirm & Generate PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BillGenerator;
